import { Box, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useCallback, useState } from 'react';
import type { FC } from 'react';
import type { DefaultPopupProps } from 'utils/props';

import Button from 'components/Button';
import ErrorText from 'components/ErrorText';
import CustomTrashIcon from 'components/Icons/CustomTrashIcon';
import Modal from 'components/Modal';
import StatusMessageBox from 'components/StatusMessageBox';

import { useAppDispatch } from 'redux-store';
import removeDevicesFromOrganizationThunk from 'redux-store/thunks/organizationOverview/removeDevicesFromOrganizationThunk';
import getOrganizationByIdThunk from 'redux-store/thunks/organizations/getOrganizationByIdThunk';

interface Props extends DefaultPopupProps {
  organizationId: number | null;
  deviceIds: number[];
  removeMultipleDevices: boolean;
}

const RemoveOrganizationDeviceModal: FC<Props> = ({
  onClose,
  organizationId,
  deviceIds,
  removeMultipleDevices,
  open,
}) => {
  const [error, setError] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const { t } = useTranslation();

  const removeDevice = useCallback(async () => {
    if (!organizationId) return;

    try {
      setLoading(true);
      const result = await dispatch(
        removeDevicesFromOrganizationThunk({
          organizationId: organizationId,
          data: {
            device_ids: deviceIds,
          },
        }),
      ).unwrap();

      if (!result.success) {
        setError(
          t(
            removeMultipleDevices
              ? 'common:pages.organization_overview.remove_devices_from_org.error'
              : 'common:pages.organization_overview.remove_device_from_org.error',
          ),
        );
      } else {
        setSuccess(true);
        setError(null);
        setTimeout(async () => {
          await dispatch(
            getOrganizationByIdThunk({
              organizationId: organizationId,
            }),
          ).unwrap();
          onClose();
          setSuccess(false);
        }, 1000);
      }
    } finally {
      setLoading(false);
    }
  }, [organizationId, dispatch, deviceIds, t, removeMultipleDevices, onClose]);

  return (
    <Modal open={open} onClose={onClose} hideCloseButton={success}>
      {!success ? (
        <Box
          height="100%"
          sx={{ overflowY: 'auto' }}
          data-testid="remove-organization-device-modal"
        >
          <Box mb={4}>
            <Typography
              variant="h2"
              textAlign="center"
              data-testid="delete-device-modal-title"
            >
              {t(
                removeMultipleDevices
                  ? 'common:pages.organization_overview.remove_devices_from_org.title'
                  : 'common:pages.organization_overview.remove_device_from_org.title',
              )}
            </Typography>
          </Box>
          <Box mb={4}>
            <Typography
              variant="body1"
              textAlign="center"
              data-testid="delete-device-modal-description"
            >
              {t(
                removeMultipleDevices
                  ? 'common:pages.organization_overview.remove_devices_from_org.description'
                  : 'common:pages.organization_overview.remove_device_from_org.description',
              )}
            </Typography>
          </Box>
          <Box display="flex" flexDirection="row" gap={1} width="100%">
            <Button
              onClick={onClose}
              color="neutral"
              fullWidth
              variant="flat"
              data-testid="cancel-button"
            >
              {t('common:cancel')}
            </Button>
            <Button
              disabled={isLoading}
              onClick={removeDevice}
              startIcon={<CustomTrashIcon />}
              fullWidth
              variant="flat"
              color="error"
              data-testid="submit"
            >
              {t('common:remove')}
            </Button>
          </Box>
          <ErrorText error={error} mt={2} />
        </Box>
      ) : (
        <StatusMessageBox
          type="success"
          message={t(
            'common:pages.organization_overview.remove_devices_from_org.success',
          )}
        />
      )}
    </Modal>
  );
};

export default RemoveOrganizationDeviceModal;
